(ns hai.tasks.view
  "Stateless styled components for rendering a task's contents, decoupled
   from how a task is loaded, stored, or mutated."
  (:require
   [garden.stylesheet]
   [hai.tasks.deadline :as deadline]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]))

;; A task's description sheet rising into view as its card presses down. Shared
;; by the home dashboard's desktop and mobile task cards.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules detail-reveal-keyframes
  (garden.stylesheet/at-keyframes
   :task-detail-in
   ["0%"   {:opacity "0" :transform "translateY(-5px)"}]
   ["100%" {:opacity "1" :transform "none"}]))

;; Numbered, schemeless, clickable list of a task's linked URLs.
(o/defstyled task-links :div
  {:display "flex" :flex-direction "column" :gap "5px"}
  [:.task-link-row
   {:display "flex" :align-items "baseline" :gap "8px"}]
  [:.task-link-num
   {:flex "0 0 auto" :min-width "12px" :text-align "right"
    :font-size "10.5px" :color t/--link-muted}]
  [:.task-link-anchor
   {:flex "1" :min-width "0"
    :color t/--task-accent
    :font-family "ui-monospace, 'SF Mono', Menlo, monospace"
    :font-size "12px"
    :text-decoration "underline"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}
   [:&:hover {:color t/--task-accent-hover}]]
  ([{:keys [links]}]
   [:<>
    (for [[idx activity] (map-indexed vector links)]
      ^{:key (or (:activity/uuid activity) (:activity/url activity))}
      [:div.task-link-row
       [:span.task-link-num (str (inc idx))]
       [:a.task-link-anchor
        {:href (:activity/url activity)
         :target "_blank"
         :rel "noopener noreferrer"
         :title (:activity/url activity)
         :on-click #(.stopPropagation %)}
        (url/url->display-label (:activity/url activity))]])]))

;; Inline deadline label: clock icon + "Due …", with the full timestamp on hover.
(o/defstyled task-deadline :span
  {:display "inline-flex" :align-items "center" :gap "5px"
   :font-size "12.5px" :font-weight "500" :line-height "1.3"
   :color t/--task-accent}
  [:svg {:flex "0 0 auto" :opacity "0.85"}]
  [:&.overdue {:color t/--card-warning-text :font-weight "600"}]
  ([{:keys [task]}]
   (when-let [label (deadline/label task)]
     [:<>
      {:class (when (deadline/overdue? task) "overdue")
       :title (deadline/tooltip task)}
      [datetime/clock-icon]
      [:span label]])))

;; A deadline as a chip: a calendar, or a clock when it has a time, beside
;; "Due …", tinted by how near it is — overdue, within a week, or further out —
;; with the full timestamp on hover. Where its row leaves it too little room
;; for the whole label it shows a shorter one ([[deadline/labels]]), and with
;; room for none, the icon alone in the same tint.
(o/defstyled deadline-chip :span
  {:display "inline-flex" :align-items "center" :gap "5px"
   :height "26px" :max-width "100%"
   :padding "0 9px"
   :border-radius "7px"
   :font-size "12px" :font-weight "700" :line-height "1"
   :white-space "nowrap"}
  ;; Every label is laid out, so each one's whole width can be read off it,
  ;; but those not shown give their width back, gap and all.
  [:.deadline-label
   {:overflow "hidden" :text-overflow "ellipsis"
    :max-width "0" :margin-left "-5px"}
   [:&.shown {:max-width "none" :margin-left "0"}]]
  ;; Stroke icons: the page applies `svg {fill: currentColor}`, which would
  ;; fill the calendar solid.
  [:svg {:flex "0 0 auto" :fill "none"}]
  [:&.overdue {:background t/--card-error-12 :color t/--card-error}]
  [:&.soon {:background t/--card-warning-12 :color t/--card-warning-text}]
  [:&.ahead {:background t/--card-info-12 :color t/--card-info}])

(defn- room-for
  "The width the row `el` stands in leaves it: the row's own, less its other
   items and the gaps beside them."
  [^js el]
  (let [row (.-parentElement el)
        gap (or (js/parseFloat (.-columnGap (js/getComputedStyle row))) 0)
        others (remove #(identical? % el) (array-seq (.-children row)))]
    (- (.-clientWidth row)
       (reduce + (map #(.-offsetWidth ^js %) others))
       (* gap (count others)))))

(defn- fitting-label
  "The index of the longest of `chip`'s labels the room its row leaves it
   takes whole, nil when none does: each at its own width, beside the icon
   and inside the chip's padding."
  [^js chip]
  (let [style (js/getComputedStyle chip)
        px #(or (js/parseFloat %) 0)
        around (+ (px (.-paddingLeft style))
                  (px (.-paddingRight style))
                  (.. chip -firstElementChild getBoundingClientRect -width)
                  (px (.-columnGap style)))
        ;; Widths come fractional and the room whole.
        room (+ (room-for chip) 0.5)]
    (first (keep-indexed (fn [index ^js label]
                           (when (<= (+ around (.-scrollWidth label)) room)
                             index))
                         (array-seq (.querySelectorAll chip ".deadline-label"))))))

(defn task-deadline-chip
  "`task`'s deadline as a [[deadline-chip]], nothing without one. It measures
   itself against its row — on every render and as the row resizes — and
   shows the longest of its labels that fits, or the icon alone."
  [{:keys [task]}]
  (r/with-let [!chip (atom nil)
               !shown (r/atom 0)
               measure! (fn [& _]
                          (when-let [^js chip @!chip]
                            (let [shown (fitting-label chip)]
                              (when (not= shown @!shown)
                                (reset! !shown shown)))))
               observer (js/ResizeObserver. measure!)
               ;; A plain span carrying the styled class by name: an Ornament
               ;; component doesn't forward the ref it is measured by.
               attach! (fn [^js el]
                         (.disconnect observer)
                         (reset! !chip el)
                         (some->> el .-parentElement (.observe observer)))]
    (when-let [labels (deadline/labels task)]
      (r/after-render measure!)
      (let [shown @!shown]
        (into [:span {:ref attach!
                      :class [(str deadline-chip)
                              (some-> (deadline/closeness task) name)]
                      :title (deadline/tooltip task)}
               (if (:task/deadline-all-day? task)
                 [icons/calendar {:size 13}]
                 [datetime/clock-icon])]
              (map-indexed (fn [index text]
                             ^{:key index}
                             [:span.deadline-label {:class (when (= index shown) "shown")}
                              text]))
              labels)))
    (finally
      (.disconnect observer))))
